import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  emptyValidatorSectionMessageInfo,
  emptyValidatorSectionMessages,
  ValidatorMessage,
  ValidatorSection,
  ValidatorSectionMessageInfo,
  ValidatorSectionMessages,
  ValidatorStatus,
} from './validatorLib';

interface SetValidatorStatusPayload {
  templateId: number;
  status: ValidatorStatus;
}

interface SetValidatorSectionMessageInfoPayload {
  templateId: number;
  data: ValidatorSectionMessageInfo;
}

interface SetPartialValidatorSectionMessagesPayload {
  templateId: number;
  section: ValidatorSection;
  messages: ValidatorMessage[];
}

interface ValidatorState {
  modal: boolean;
  modalContent: boolean;
  template: Partial<
    Record<
      number,
      {
        status: ValidatorStatus;
        sectionMessages: ValidatorSectionMessages;
        sectionMessageInfo: ValidatorSectionMessageInfo;
      }
    >
  >;
}

const assureTemplateValidatorState = (state: ValidatorState, templateId: number) => {
  state.template[templateId] ??= {
    status: ValidatorStatus.RUNNING,
    sectionMessages: { ...emptyValidatorSectionMessages },
    sectionMessageInfo: { ...emptyValidatorSectionMessageInfo },
  };

  return state.template[templateId];
};

const initialState: ValidatorState = {
  modal: false,
  modalContent: false,
  template: {},
};

export const validatorSlice = createSlice({
  name: 'validator',
  initialState,
  reducers: {
    toggleValidatorModal: state => {
      const willOpen = !state.modal;
      if (willOpen) {
        state.modal = true;
        state.modalContent = true;
      } else {
        state.modal = false;
      }
    },
    toggleValidatorModalContent: state => {
      state.modalContent = !state.modalContent;
    },
    setValidatorStatus: (state, action: PayloadAction<SetValidatorStatusPayload>) => {
      const templateData = assureTemplateValidatorState(state, action.payload.templateId);
      templateData.status = action.payload.status;
    },
    setPartialValidatorSectionMessages: (
      state,
      action: PayloadAction<SetPartialValidatorSectionMessagesPayload>,
    ) => {
      const templateData = assureTemplateValidatorState(state, action.payload.templateId);
      templateData.sectionMessages[action.payload.section] = action.payload.messages;
    },
    setValidatorSectionMessageInfo: (
      state,
      action: PayloadAction<SetValidatorSectionMessageInfoPayload>,
    ) => {
      const templateData = assureTemplateValidatorState(state, action.payload.templateId);
      templateData.sectionMessageInfo = action.payload.data;
    },
    resetState: () => ({
      ...initialState,
    }),
  },
});

export const {
  toggleValidatorModal,
  toggleValidatorModalContent,
  setValidatorStatus,
  setPartialValidatorSectionMessages,
  setValidatorSectionMessageInfo,
} = validatorSlice.actions;

export const validatorReducer = validatorSlice.reducer;
